<!-- With a report open the page locks to the viewport (minus the 4rem navbar) so the ONLY
     scrollbars are the report's internal ones — no page/body scroll behind it. -->
<div class="bg-gray-50 dark:bg-surface-0 px-4 py-6 md:px-8"
  [class]="reportSid() ? 'h-[calc(100vh-4rem)] overflow-hidden' : 'min-h-screen'">
  <!-- ── system alert (e.g. Gemini rate limit / spent allowance) — TEMPORARILY HIDDEN ──
       Fixed top-center so it's unmissable from any tab (and over an open report).
       Sticky until dismissed; the backend throttles re-broadcasts PER KIND.
       Severity decides colour and motion: 'error' pulses red because something is
       broken, 'warn' sits amber and still because it already recovered — a demo
       that reconnected through a Google rate limit used to raise the same shouting
       red banner as a dead product. The session id is shown so the affected call
       can be found in the table below.
  @if (store.systemAlert(); as alert) {
  <div class="fixed left-1/2 top-20 z-[60] -translate-x-1/2 w-max max-w-[90vw]" role="alert"
    [attr.aria-live]="alert.severity === 'warn' ? 'polite' : 'assertive'">
    <div
      class="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg ring-1"
      [class]="alert.severity === 'warn'
        ? 'bg-amber-600 shadow-amber-900/40 ring-amber-300/60'
        : 'bg-red-600 shadow-red-900/40 ring-red-300/60 animate-pulse'"
      [title]="alert.detail || ''">
      <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span class="truncate">{{ alert.message }}</span>
      @if (alert.sessionId; as sid) {
      <span class="shrink-0 rounded-md bg-white/20 px-1.5 py-0.5 font-mono text-[11px] font-normal"
        [title]="sid">{{ sid.slice(0, 8) }}</span>
      }
      <button type="button" (click)="store.dismissSystemAlert()" aria-label="Dismiss alert"
        class="shrink-0 rounded-full p-0.5 text-white/80 hover:bg-white/20 hover:text-white">
        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  </div>
  }
  -->
  <!-- The dashboard is HIDDEN (not destroyed) while a report is open, so filters,
       pagination and the live store all survive the round-trip. The report renders
       inline — the shell sidebar/navbar stay visible and usable. -->
  <div [class.hidden]="reportSid()">
    <!-- ── header ── -->
    <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <div class="flex items-center gap-2.5">
          <h1 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">{{ allProductsPanel ? 'Pre-Join Live' : 'Tracking Demo' }}</h1>
          @if (!allProductsPanel) {
          <span
            class="inline-flex items-center gap-1.5 rounded-full bg-white dark:bg-surface-1 px-3 py-1 text-sm ring-1 ring-[#EAEAEA] dark:ring-line"
            [class]="connectionLabel().cls">
            <span class="h-1.5 w-1.5 rounded-full" [class]="connectionLabel().dot"></span>
            {{ connectionLabel().text }}
          </span>
          }
        </div>
        <p class="text-sm text-gray-500">{{ allProductsPanel
          ? 'Visitors on the demo setup form right now, for every product — watch any of them live.'
          : 'Live monitoring & session security for every AI demo.' }}</p>
      </div>
      @if (!allProductsPanel) {
      <div class="flex items-center gap-2 text-sm">
        <button type="button" (click)="store.resync()" [disabled]="store.loading()"
          class="inline-flex items-center gap-1.5 rounded-lg border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-3 py-1 text-gray-600 dark:text-ink-soft hover:bg-gray-100 dark:hover:bg-line disabled:opacity-60 disabled:cursor-wait">
          @if (store.loading()) {
          <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          }
          Refresh
        </button>
        <!-- Own availability: ONLINE = "a live demo may ring me". The ringing modal is mounted in
           the private layout, so an agent who goes online here is reachable across the whole CRM. -->
        <app-presence-toggle></app-presence-toggle>
        <!-- Dummy/Live data toggle: server-side filter on every listing + the metrics.
           Dummy = the team's own test demos (internal-domain business email). -->
        <div
          class="inline-flex items-center rounded-full bg-white dark:bg-surface-1 p-0.5 ring-1 ring-[#EAEAEA] dark:ring-line"
          role="group" aria-label="Data type">
          <button type="button" (click)="store.setDataType('live')"
            class="rounded-full px-3 py-0.5 text-xs font-medium transition-colors" [class]="store.dataType() === 'live'
            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
            : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'">
            Live
          </button>
          <button type="button" (click)="store.setDataType('dummy')"
            class="rounded-full px-3 py-0.5 text-xs font-medium transition-colors" [class]="store.dataType() === 'dummy'
            ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
            : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'">
            Dummy
          </button>
        </div>
      </div>
      }
    </div>

    <!-- ── product tabs (only on the all-products "Prism" panel) ── -->
    @if (allProductsPanel) {
    <div class="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Product">
      @for (p of productTabs; track p.key) {
      <button type="button" role="tab" [attr.aria-selected]="activeProduct() === p.key" (click)="selectProduct(p.key)"
        class="rounded-lg px-4 py-2 text-sm font-medium transition-colors"
        [class]="activeProduct() === p.key
          ? 'bg-fuchsia-600 text-white shadow-sm'
          : 'border border-[#EAEAEA] bg-white text-slate-600 hover:bg-gray-100 dark:border-line dark:bg-surface-1 dark:text-ink-soft dark:hover:bg-line'">
        {{ p.label }}
      </button>
      }
    </div>
    }

    @if (allProductsPanel) {
    <!-- The Prism panel is the pre-join live board only: keyed on the product so the
         list reloads scoped to the tab picked above. -->
    @for (productKey of [activeProduct()]; track productKey) {
    <app-prejoin-live></app-prejoin-live>
    }
    } @else {
    <!-- ── metrics ── -->
    <app-session-metrics [metrics]="store.metrics()" class="block mb-5"></app-session-metrics>

    <!-- ── tabs ── -->
    <div class="mb-4 flex gap-1 border-b border-gray-200 dark:border-white/10">
      @for (t of tabs; track t) {
      <button type="button" (click)="tab.set(t)"
        class="relative px-4 py-2.5 text-sm font-medium capitalize transition-colors"
        [class]="tab() === t ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'">
        {{ t === 'failed' ? 'Failed queries' : t === 'dumps' ? 'Pre-join dumps' : t }}
        @if (tab() === t) { <span class="absolute inset-x-3 -bottom-px h-0.5 rounded bg-indigo-500"></span> }
      </button>
      }
    </div>

    <!-- Keyed on the product so every tab below re-creates (and reloads, scoped to it)
         when the all-products panel switches product. -->
    @for (productKey of [activeProduct()]; track productKey) {
    <!-- ── LIVE ── -->
    @if (tab() === 'live') {
    <div class="mb-3 flex flex-wrap items-center gap-2">
      <input [ngModel]="store.search()" (ngModelChange)="store.search.set($event)"
        placeholder="Search company, host, IP, browser…"
        class="w-72 rounded-lg border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-3 py-1.5 text-sm text-slate-700 dark:text-ink-soft placeholder:text-slate-400 dark:placeholder:text-ink-subtle outline-none focus:border-indigo-400 dark:focus:border-indigo-500/60" />
      <!-- [color-scheme:dark] makes the NATIVE popup list render dark too (options
           can't take Tailwind styling on most platforms — the scheme is the real fix). -->
      <select [ngModel]="store.statusFilter()" (ngModelChange)="store.statusFilter.set($event)"
        class="cursor-pointer rounded-lg border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-3 py-1.5 text-sm text-slate-700 dark:text-ink-soft outline-none focus:border-indigo-400 dark:focus:border-indigo-500/60 [color-scheme:light] dark:[color-scheme:dark]">
        @for (opt of statusFilterOptions; track opt.value) {
        <option [value]="opt.value" class="bg-white text-slate-700 dark:bg-surface-1 dark:text-ink-soft">{{ opt.label
          }}</option>
        }
      </select>
      <span class="ml-auto text-xs text-gray-400">{{ store.filteredSessions().length }} shown</span>
    </div>
    <app-live-session-table [sessions]="store.filteredSessions()" [now]="store.now()" [joinEnabled]="joinEnabled"
      (rowClick)="onRowClick($event)" (action)="onAction($event)"></app-live-session-table>
    }

    <!-- ── HISTORY ── -->
    @if (tab() === 'history') { <app-session-history (reportRequested)="openReport($event)"></app-session-history> }

    <!-- ── FAILED QUERIES ── -->
    @if (tab() === 'failed') { <app-failed-queries></app-failed-queries> }

    <!-- ── BLACKLIST ── -->
    @if (tab() === 'blacklist') { <app-blacklist-manager></app-blacklist-manager> }

    <!-- ── AUDIT ── -->
    @if (tab() === 'audit') { <app-audit-logs></app-audit-logs> }

    <!-- ── INSIGHTS (cross-session behavioural aggregate + click heatmap) ── -->
    @if (tab() === 'insights') { <app-insights-dashboard></app-insights-dashboard> }

    <!-- ── PRE-JOIN DUMPS (never-joined visitors: rrweb replay + heatmap per visit) ── -->
    @if (tab() === 'dumps') { <app-prejoin-dumps></app-prejoin-dumps> }
    }
    }
  </div>

  <!-- ── full session report (History detail — inline, shell stays visible) ── -->
  @if (reportSid(); as sid) {
  <app-session-report [sessionId]="sid" (closed)="closeReport()"></app-session-report>
  }

  <!-- ── "Watch live screen": the host's screen, live (rrweb — lazy-loaded) ── -->
  @if (screenSession(); as s) {
    @defer {
      <app-live-screen-viewer [session]="s" (closed)="screenSession.set(null)"></app-live-screen-viewer>
    }
  }

  <!-- ── details / participants / covert live-view panel ── -->
  <app-session-details-panel [open]="panel()" (closed)="closePanel()" (disconnectRequested)="confirmDisconnect($event)"
    (blacklistRequested)="confirmBlacklist($event)"></app-session-details-panel>
</div>